
import { defineComponent, PropType } from 'vue';
import { mapGetters } from 'vuex';
import PercentInput from '@/components/ui/PercentInput.vue';
import CurrencyInput from '@/components/ui/CurrencyInput.vue';
import { SalePriceRow } from '@/store/proposal/proposal.module';
import { SalePrice } from '@/model/sale-price';
import { useField, useForm } from 'vee-validate';
import * as yup from 'yup';

export default defineComponent({
  name: "SalePriceColumn",
  components: {PercentInput, CurrencyInput}, 
  setup(props) {
    useForm({
      validationSchema: yup.object({
        grossMargin: yup.number().required().moreThan(0).lessThan(101)
      })
    });

    const {value: grossMarginField, meta: grossMarginMeta} = useField('grossMargin', {}, {
      initialValue: +props.weightBracket.grossMargin,
    });

    const { value: priceField, meta: priceMeta } = useField('price', {}, {
    initialValue: +props.weightBracket.price.amount / 100,
    });

    return {
      grossMarginField,
      grossMarginMeta,
      priceField,
      priceMeta
    }
  },
  data(): { salePrice: SalePrice } {
    return {
      salePrice: this.weightBracket
    }
  },
  props: {
    weightBracket: {
      type: Object as PropType<SalePrice>,
      required: true
    },
    salePriceRow: {
      type: Object as PropType<SalePriceRow>,
      required: true
    },
    isDisabled: {
      type: Boolean,
      required: true
    }
  },
  watch: {
    rowGrossMargin(newValue: number): void {
      this.grossMargin = newValue;
    },
    // Watch the specific weightBracket data
    'weightBracket.grossMargin': {
      handler(newValue: number): void {
        if (this.isProposalEditable && this.grossMarginField !== newValue) {
          this.grossMarginField = newValue;
        }
      },
      immediate: true
    },
    'weightBracket.price.amount': {
      handler(newValue: number | string): void {
        if (this.isProposalEditable && typeof newValue === 'number') {
          const priceValue = newValue / 100;
          if (this.priceField !== priceValue) {
            this.priceField = priceValue;
          }
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters(['showCostPrice', 'showGrossMargin', 'proposal', 'isProposalEditable']),
    rowGrossMargin(): number {
      return this.salePriceRow.grossMargin;
    },
    grossMargin: {
      get(): string | number | undefined {
        if (!this.isProposalEditable) {
          return +this.salePrice.grossMargin;
        }

        return this.salePrice.grossMargin;
      },
      set(value: number): void {
        this.grossMarginField = value;
        this.salePrice.grossMargin = value;
        this.updatePriceFromMargin(value);
      }
    },
    costPrice(): number | string {
      if (this.salePrice.costPrice.amount === null) {
        return '-';
      }
      return this.$n(Number(this.salePrice.costPrice.amount) / 100, 'decimal');
    },
    currencySymbol(): string {
    return this.$te(`general.currency_symbol.${this.salePrice.price.currency}`)
      ? this.$t(`general.currency_symbol.${this.salePrice.price.currency}`)
      : this.salePrice.price.currency;
    },
    price: {
      get(): number | string {
        if (!this.isProposalEditable) {
          const amount = this.salePrice.price.amount;
          if (amount === null) return '-';
          return this.$n(Number(amount) / 100, 'decimal');
        }
        return this.priceField;
      },
      set(value: number): void {
        this.priceField = value;
        this.salePrice.price.amount = Math.round(value * 100);
        this.updateMarginFromPrice(value);
      }
    }
  },
  methods: {
    updatePriceFromMargin(margin: number): void {
      const costPrice = this.salePrice.costPrice.amount;
      if (costPrice === null) {
        this.salePrice.price.amount = 0;
        this.priceField = 0;
        return;
      }
      const costPriceNumber = Number(costPrice);
      const newPrice = Math.round(costPriceNumber * (margin + 100) / 100);
      this.salePrice.price.amount = newPrice;
      
      // Update the price field without triggering its setter
      this.priceField = newPrice / 100;
    },
    updateMarginFromPrice(price: number): void {
      const costPrice = this.salePrice.costPrice.amount;
      if (costPrice === null) {
        this.salePrice.grossMargin = 0;
        return;
      }
      const costPriceNumber = Number(costPrice);
      const salePriceAmount = price * 100;
      const calculatedMargin = (salePriceAmount - costPriceNumber) * 100 / costPriceNumber;
      const roundedMargin = Math.round(calculatedMargin * 100) / 100;
      
      this.salePrice.grossMargin = roundedMargin;
      
      // Update the margin field without triggering its setter
      this.grossMarginField = roundedMargin;
    }
  }
})
